import type {
  ContentTypeDefinition,
  ContentTypePropertyConfig,
  ContentTypePropertySchema,
  WithOptionalInternalProps,
} from '../../lib/flotiq-client/types/api-response';

import moment from 'moment';
import * as Y from 'yjs';

import { getDefaultValueForType } from '../../lib/helpers';

import { updateYjsText } from './updateField';

type PathSegment = string | number;
export type YjsParent = Y.Array<any> | Y.Map<any>;

export const getYjsInstance = <T>(
  parent: YjsParent,
  key: string | number,
): T | undefined => {
  return parent instanceof Y.Array
    ? parent.get(key as number)
    : parent.get(key as string);
};

export const setInYjsParent = (
  parent: YjsParent,
  key: string | number,
  value: unknown,
) => {
  if (parent instanceof Y.Array) {
    const index = key as number;
    if (index < parent.length) parent.delete(index, 1);
    parent.insert(index, [value]);
    return;
  }

  parent.set(key as string, value);
};

const parseFieldPath = (fieldName: string): PathSegment[] => {
  return fieldName
    .split(/[[.\]]/)
    .filter(Boolean)
    .map((segment) => (/^\d+$/.test(segment) ? +segment : segment));
};

const isValidType = (
  value: unknown,
  fieldSchema: ContentTypePropertySchema,
) => {
  if (typeof value === 'undefined' || value === null) return false;

  if ((fieldSchema as { $ref?: string }).$ref) {
    return (
      typeof value === 'object' &&
      !Array.isArray(value) &&
      value !== null &&
      'dataUrl' in value
    );
  }

  if (fieldSchema.type === 'array') {
    if (!Array.isArray(value)) return false;
    if (fieldSchema.items?.$ref) {
      return !value.some((item) => !item?.dataUrl);
    }
    return true;
  }
  if (fieldSchema.type === 'object') {
    return typeof value === 'object' && !Array.isArray(value);
  }
  return typeof value === fieldSchema.type;
};

const getFieldValue = (
  fieldSchema: ContentTypePropertySchema,
  fieldProperty: ContentTypePropertyConfig | undefined,
  value: unknown,
  allowDefaults = false,
) => {
  if (isValidType(value, fieldSchema)) {
    return value;
  }

  let defaultValue = fieldSchema.default;
  if (fieldProperty?.inputType === 'dateTime' && fieldSchema?.default) {
    defaultValue = moment(fieldSchema.default, moment.ISO_8601, true).isValid()
      ? fieldSchema.default
      : '';
  }

  if (isValidType(defaultValue, fieldSchema) && allowDefaults) {
    return defaultValue;
  }

  return getDefaultValueForType(fieldSchema.type);
};

const createYjsValue = (
  schema: ContentTypePropertySchema,
  property?: ContentTypePropertyConfig,
) => {
  if (schema.type === 'string') {
    return new Y.Text();
  }

  if (schema.type === 'array') {
    return new Y.Array<any>();
  }

  if (schema.type === 'object' && property?.inputType !== 'block') {
    return new Y.Map<any>();
  }
};

export const syncYjsMapContent = (
  yMap: Y.Map<any>,
  value: unknown,
  schema?: Record<string, ContentTypePropertySchema>,
  properties?: Record<string, ContentTypePropertyConfig>,
  allowDefaults = false,
) => {
  if (!schema) return;
  if (!value || typeof value !== 'object' || Array.isArray(value)) return;

  Object.keys(schema).forEach((key) => {
    syncYjsEntry(
      yMap,
      key,
      schema[key],
      properties?.[key],
      (value as Record<string, unknown>)[key],
      allowDefaults,
    );
  });
};

const syncYjsArrayContent = (
  yArray: Y.Array<any>,
  arraySchema: ContentTypePropertySchema,
  arrayProperty: ContentTypePropertyConfig | undefined,
  value: unknown,
  allowDefaults = false,
) => {
  if (!value || !Array.isArray(value)) return;

  /**
   * @todo Some plugins have wrong schema (dropdown multiple defined without items).
   * Fallback to { type: 'string' } keeps those plugin schemas compatible.
   */
  value.forEach((item, index) => {
    syncYjsEntry(
      yArray,
      index,
      (arraySchema.items ?? { type: 'string' }) as ContentTypePropertySchema,
      arrayProperty,
      item,
      allowDefaults,
    );
  });

  if (yArray.length > value.length) {
    yArray.delete(value.length, yArray.length - value.length);
  }
};

const ensureArrayEntry = (
  parent: YjsParent,
  key: string | number,
  schema: ContentTypePropertySchema,
  property: ContentTypePropertyConfig | undefined,
) => {
  const yjsInstance = getYjsInstance<Y.Array<any>>(parent, key);
  if (yjsInstance) return yjsInstance;

  const yArray = createYjsValue(schema, property) as Y.Array<any>;
  setInYjsParent(parent, key, yArray);
  return yArray;
};

const ensureMapEntry = (
  parent: YjsParent,
  key: string | number,
  schema: ContentTypePropertySchema,
  property?: ContentTypePropertyConfig,
) => {
  const yjsInstance = getYjsInstance<Y.Map<any>>(parent, key);
  if (yjsInstance) return yjsInstance;

  const yMap = createYjsValue(schema, property) as Y.Map<any>;
  setInYjsParent(parent, key, yMap);
  return yMap;
};

const ensureArrayIndex = (
  yArray: Y.Array<any>,
  index: number,
  arraySchema: ContentTypePropertySchema,
) => {
  const yjsInstance = getYjsInstance(yArray, index);
  if (yjsInstance) return yjsInstance;

  yArray.insert(index, [
    createYjsValue(arraySchema.items as ContentTypePropertySchema),
  ]);
};

const syncArrayPath = (
  yArray: Y.Array<any>,
  arraySchema: ContentTypePropertySchema,
  arrayProperty: ContentTypePropertyConfig | undefined,
  path: PathSegment[],
  value: unknown,
) => {
  const [segment, ...rest] = path;

  if (typeof segment !== 'number') return;

  const itemsSchema = arraySchema.items;

  ensureArrayIndex(yArray, segment, arraySchema);

  if (rest.length === 0 || itemsSchema?.type !== 'object') {
    syncYjsEntry(
      yArray,
      segment,
      itemsSchema as ContentTypePropertySchema,
      undefined,
      value,
    );
    return;
  }

  const itemMap = ensureMapEntry(
    yArray,
    segment,
    itemsSchema as ContentTypePropertySchema,
  );

  syncMapPath(itemMap, arraySchema, arrayProperty, rest, value);
};

const syncMapPath = (
  yMap: Y.Map<any>,
  objectSchema: ContentTypePropertySchema,
  objectProperty: ContentTypePropertyConfig | undefined,
  path: PathSegment[],
  value?: unknown,
) => {
  const [segment, ...rest] = path;

  if (typeof segment !== 'string') return;

  const childSchema =
    objectSchema?.items?.properties?.[segment] ??
    objectSchema?.properties?.[segment];

  if (!childSchema) return;

  const childProperty = objectProperty?.items?.propertiesConfig?.[segment];

  if (rest.length === 0) {
    syncYjsEntry(yMap, segment, childSchema, childProperty, value);
    return;
  }

  if (childSchema.type === 'array') {
    const childArray = ensureArrayEntry(
      yMap,
      segment,
      childSchema,
      childProperty,
    );

    syncArrayPath(childArray, childSchema, childProperty, rest, value);
    return;
  }

  if (childSchema.type === 'object' && childProperty?.inputType !== 'block') {
    const childMap = ensureMapEntry(yMap, segment, childSchema, childProperty);

    syncMapPath(childMap, childSchema, childProperty, rest, value);
    return;
  }

  syncYjsEntry(yMap, segment, childSchema, childProperty, value);
};

export function syncYjsEntry(
  parent: YjsParent,
  key: string | number,
  schema: ContentTypePropertySchema,
  property: ContentTypePropertyConfig | undefined,
  value: unknown,
  allowDefaults = false,
) {
  const yjsInstance = getYjsInstance(parent, key);
  const normalizedValue = getFieldValue(schema, property, value, allowDefaults);

  if (schema.type === 'string') {
    if (yjsInstance) {
      updateYjsText(
        {
          parent: parent as any,
          key: key as never,
        },
        normalizedValue as string,
      );
      return;
    }

    setInYjsParent(parent, key, new Y.Text(normalizedValue as string));
    return;
  }

  if (schema.type === 'array') {
    const yArray = ensureArrayEntry(parent, key, schema, property);
    syncYjsArrayContent(
      yArray,
      schema,
      property,
      normalizedValue,
      allowDefaults,
    );
    return;
  }

  if (schema.type === 'object' && property?.inputType !== 'block') {
    const yMap = ensureMapEntry(parent, key, schema, property);
    syncYjsMapContent(
      yMap,
      normalizedValue,
      schema.properties,
      property?.items?.propertiesConfig,
      allowDefaults,
    );
    return;
  }

  setInYjsParent(parent, key, normalizedValue);
}

export const syncCollaborativeFieldValue = (
  contentType: WithOptionalInternalProps<ContentTypeDefinition>,
  fieldName: string,
  fieldValue: unknown,
  yjsDocument: Y.Doc,
) => {
  const itemsSchema = contentType.schemaDefinition.allOf[1].properties;
  const itemsProperties = contentType.metaDefinition.propertiesConfig;

  const path = parseFieldPath(fieldName);
  const [rootSegment, ...rest] = path;
  const rootSchema = itemsSchema[rootSegment];

  if (!rootSchema) return;

  const rootProperty = itemsProperties[rootSegment];
  const valsMap = yjsDocument.getMap('vals');

  yjsDocument.transact(() => {
    if (rest.length === 0) {
      syncYjsEntry(valsMap, rootSegment, rootSchema, rootProperty, fieldValue);
      return;
    }

    if (rootSchema.type === 'array') {
      const yArray = ensureArrayEntry(
        valsMap,
        rootSegment,
        rootSchema,
        rootProperty,
      );

      syncArrayPath(yArray, rootSchema, rootProperty, rest, fieldValue);
      return;
    }

    if (rootSchema.type === 'object' && rootProperty?.inputType !== 'block') {
      const yMap = ensureMapEntry(
        valsMap,
        rootSegment,
        rootSchema,
        rootProperty,
      );

      syncMapPath(yMap, rootSchema, rootProperty, rest, fieldValue);
      return;
    }

    syncYjsEntry(valsMap, rootSegment, rootSchema, rootProperty, fieldValue);
  });
};
